import React, { useRef, useEffect, useState } from "react";
import DateRangePresets from "../../filter/TabFilterModal/DateRangePresets";

const GRANULARITIES = [
  { id: "day",   label: "Tag" },
  { id: "week",  label: "Woche" },
  { id: "month", label: "Monat" },
  { id: "year",  label: "Jahr" },
];

function DateInput({ value, onChange, min, max, placeholder }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handle = (e) => onChange(e.target.value);
    el.addEventListener("change", handle);
    return () => el.removeEventListener("change", handle);
  }, [onChange]);

  return (
    <input
      ref={ref}
      type="date"
      defaultValue={value}
      min={min}
      max={max}
      placeholder={placeholder}
      className="px-1.5 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
    />
  );
}

const btnBase =
  "px-2 py-1 text-xs rounded-md border transition-colors whitespace-nowrap";
const btnActive = "bg-blue-600 text-white border-blue-600";
const btnInactive = "bg-white text-gray-700 border-gray-300 hover:bg-gray-50";

const DateScopeControl = ({
  viewMode,
  onViewModeChange,
  granularity,
  onGranularityChange,
  dateRangeStart,
  onDateRangeStartChange,
  dateRangeEnd,
  onDateRangeEndChange,
  onApplyDateRange,
  loading,
}) => {
  const [pendingStart, setPendingStart] = useState(dateRangeStart || "");
  const [pendingEnd, setPendingEnd] = useState(dateRangeEnd || "");

  useEffect(() => { setPendingStart(dateRangeStart || ""); }, [dateRangeStart]);
  useEffect(() => { setPendingEnd(dateRangeEnd || ""); }, [dateRangeEnd]);

  const handleApply = () => {
    onDateRangeStartChange(pendingStart);
    onDateRangeEndChange(pendingEnd);
    onApplyDateRange(pendingStart, pendingEnd);
  };

  const handlePresetChange = (value) => {
    const start = (value?.startDate || "").slice(0, 10);
    const end = (value?.endDate || "").slice(0, 10);
    setPendingStart(start);
    setPendingEnd(end);
    onDateRangeStartChange(start);
    onDateRangeEndChange(end);
    onApplyDateRange(start, end);
  };

  return (
    <div className="flex flex-col gap-1.5 px-1 py-1 text-xs">
      <div className="flex items-center gap-1.5 flex-wrap">
        {/* View mode pill toggle */}
        <div className="flex gap-0.5 bg-gray-100 rounded-md p-0.5">
          {[
            { id: "granularity", label: "Granularität" },
            { id: "daterange",   label: "Zeitraum" },
          ].map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => onViewModeChange(id)}
              className={`px-2 py-0.5 text-xs rounded transition-colors ${
                viewMode === id
                  ? "bg-white text-gray-900 shadow-sm font-medium"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Granularity buttons — always visible in both modes */}
        <div className="flex items-center gap-1">
          {GRANULARITIES.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              disabled={loading}
              onClick={() => onGranularityChange(id)}
              className={`${btnBase} ${granularity === id ? btnActive : btnInactive}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Date range pickers — only in Zeitraum mode */}
        {viewMode === "daterange" && (
          <div className="flex items-center gap-1">
            <DateInput
              value={pendingStart}
              onChange={setPendingStart}
              placeholder="Von"
            />
            <span className="text-gray-400">–</span>
            <DateInput
              value={pendingEnd}
              onChange={setPendingEnd}
              placeholder="Bis"
            />
            <button
              type="button"
              disabled={loading || !pendingStart || !pendingEnd}
              onClick={handleApply}
              className="px-2 py-1 text-xs bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? "Lädt…" : "Anwenden"}
            </button>
          </div>
        )}
      </div>

      {viewMode === "daterange" && (
        <DateRangePresets
          value={{ startDate: pendingStart || null, endDate: pendingEnd || null, enabled: true }}
          onChange={handlePresetChange}
        />
      )}
    </div>
  );
};

export default DateScopeControl;
